import cycleIcon from '@xero/xui-icon/icons/cycle';
import warningIcon from '@xero/xui-icon/icons/warning';
import { XUIBannerAction } from '@xero/xui/react/banner';
import XUIButton from '@xero/xui/react/button';
import XUIIcon from '@xero/xui/react/icon';
import { XUILoader } from '@xero/xui/react/loader';
import { XUIBreadcrumbTrail, XUIPageHeader } from '@xero/xui/react/pageheader';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ActivityTable from '../../components/ActivityTable';
import InformationBanner from '../../components/InformationBanner';
import Overview from '../../components/Overview';
import TaxDisclaimer from '../../components/TaxDisclaimer';
import { setVisible } from '../../helpers/header';
import integrationRoutes, { Pages } from '../../helpers/routes';
import { eventSelectors, getEventsAction } from '../../redux/slices/EventSlice';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { getSettingsAction, settingsSelectors } from '../../redux/slices/SettingsSlice';
import { userSelectors } from '../../redux/slices/UserSlice';
import { useAppDispatch } from '../../redux/store';
import { installationStates, integrationTypes } from '../../static/constants';
import lang from '../../static/lang/en-NZ';
import './Connected.css';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import XUITag from '@xero/xui/react/tag';
import externalIcon from '@xero/xui-icon/icons/external';
import DisconnectAppModal from '../../components/DisconnectAppModal';
import PendingCancellationBanner from '../../components/PendingCancellationBanner';
import { organisationSelectors } from '../../redux/slices/OrganisationSlice';
import { translateWithIntegrationName } from '../../helpers/translate';
import { convertTimezoneDisplayDate } from '../../helpers/date';
import DeleteModal from '../../components/DeleteModal';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { getFeatureFlagValue } from '../../helpers/getFeatureFlagValue';
import { isProd } from '../../helpers/env';

interface ILDProps {
    ldClient?: LDClient;
    flags?: any;
}

const SECOND = 1000;
const FIVE_SECONDS = SECOND * 5;
const TWO_MINUTES = SECOND * 120;

export interface IConnectedProps {
    isSyncing: boolean;
    onDisconnectClick: (cancelSubscription?: boolean) => void;
    onSettingsClick: () => void;
    onUpdateAppClick: () => void;
    onPaidConnectClick: () => void;
}

const Connected: FC<IConnectedProps & ILDProps> = ({ ldClient, flags, ...props }) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const isFetchingSettings = useSelector(settingsSelectors.isFetching);
    const events = useSelector(eventSelectors.events);
    const user = useSelector(userSelectors.user);
    const organisation = useSelector(organisationSelectors.organisation);

    const billing = installation.billing;
    const routes = integrationRoutes.get(installation.integrationType);
    const integrationLang: any = lang[installation.integrationType];
    let poller: number | undefined;

    // Note - a test expects this to be the first useState in the file in order to properly mock
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [isConnectingToPaid, setIsConnectingToPaid] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [isShopifyDecommissionBannerEnabled, setIsShopifyDecommissionBannerEnabled] = useState(false);

    useEffect(() => {
        document.body.style.background = '#fff';
        setVisible(true);
        dispatch(getSettingsAction());
        getIntegrationEvents();
        // Assignments to the 'poller' variable from inside React Hook useEffect will be lost after each render.
        // To preserve the value over time, store it in a useRef Hook and keep the mutable value in the '.current' property.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
        poller = window.setInterval(async () => getIntegrationEvents(), FIVE_SECONDS);
        setTimeout(deletePoller, TWO_MINUTES);
        return deletePoller;
    }, []);

    useEffect(() => {
        if (installation.integrationType !== integrationTypes.SHOPIFY) {
            return;
        }

        const f = async () => {
            const bannerEnabled = await getFeatureFlagValue(
                ldClient!,
                'outbound-shopify-integration-decommission-cancel-subscriptions',
                user.xero_userid,
                {
                    tenantId: organisation.id,
                    tenantRegion: organisation.region!,
                    integrationType: installation.integrationType,
                }
            );

            setIsShopifyDecommissionBannerEnabled(bannerEnabled ?? false);
        };

        f();
    }, [ldClient, user.xero_userid, organisation.id, organisation.region, installation.integrationType]);

    const hideShopifyNewConnectionActions =
        installation.integrationType === integrationTypes.SHOPIFY;


    const decommissionBannerCopy = integrationLang.ACCESS_PAGE.DECOMMISSION_BANNER;

    const shopifyDecommissionBanner = isShopifyDecommissionBannerEnabled && (
        <div className='xui-margin-horizontal' data-testid='connected-shopify-decommission-banner'>
            <InformationBanner
                header={
                    <span className='xui-heading-medium pending-cancellation-banner-header'>
                        <XUIIcon icon={warningIcon} color='black' className='xui-margin-right-small' />
                        {decommissionBannerCopy.HEADING}
                    </span>
                }
                bannerMessage={
                    <>
                        {decommissionBannerCopy.BODY(billing?.hasBought ?? false)}
                        <br />
                        {decommissionBannerCopy.BODY_ACTION}
                    </>
                }
                sentiment={'negative'}
                displayCloseButton={false}
                action={
                    <div data-testid='connected-shopify-decommission-action'>
                        <XUIBannerAction isLink={true} href={decommissionBannerCopy.ACTION_BUTTON_URL(isProd())}>
                            {decommissionBannerCopy.ACTION_BUTTON_TEXT} <XUIIcon icon={externalIcon} />
                        </XUIBannerAction>
                    </div>
                }
                displayWarningIcon={false}
            />
        </div>
    );

    const renderUpdatePanel = () => {
        // If there is no wording, don't show the banner
        if (!integrationLang.UPDATE.HEADING) {
            return null;
        }

        const updateBannerHeading = integrationLang.UPDATE.HEADING.replace(
            '{integrationType}',
            installation.displayName
        );
        const updateBannerContentPartOne = integrationLang.UPDATE.CONTENT_PART_1.replace(
            /{integrationType}/g,
            installation.displayName
        );
        const updateBannerContentPartTwo = integrationLang.UPDATE.CONTENT_PART_2;
        const updateBannerContentUrl = integrationLang.UPDATE.CONTENT_URL;
        const updateBannerContentDisplayLink = integrationLang.UPDATE.CONTENT_DISPLAY_LINK;

        const isShopifyIntegration = installation.integrationType === integrationTypes.SHOPIFY;

        return (
            <div className='xui-margin-horizontal'>
                <InformationBanner
                    qaHook='connected-updateApp-banner'
                    bannerMessage={
                        <>
                            <span>
                                <span className='xui-heading-xlarge'>
                                    <XUIIcon icon={cycleIcon} color='black-faint' className='xui-margin-right-small' />
                                    {updateBannerHeading}
                                </span>
                                <br />
                                <span className='xui-text-secondary'>
                                    {updateBannerContentPartOne}
                                    <br />
                                    {updateBannerContentPartTwo}
                                    <a href={updateBannerContentUrl}>{updateBannerContentDisplayLink}</a>
                                </span>
                            </span>
                        </>
                    }
                    sentiment={'positive'}
                    displayCloseButton={!isShopifyIntegration}
                    action={
                        isShopifyIntegration ? (
                            <XUIButton onClick={onUpdateAppClick} size='small' variant='borderless-main'>
                                {lang.BUTTONS.SETUP_NOW}
                            </XUIButton>
                        ) : undefined
                    }
                />
            </div>
        );
    };

    const getIntegrationEvents = (): void => {
        // don't poll if they have over 1000 events
        if (poller && events && events.length >= 1000) {
            deletePoller();
            return;
        }

        dispatch(getEventsAction());
    };

    const onDisconnectClick = (cancelSubscription: boolean = false): void => {
        setIsDisconnecting(true);

        deletePoller();
        props.onDisconnectClick(cancelSubscription);
    };

    const onUpdateAppClick = (): void => {
        props.onUpdateAppClick();
    };

    const onSubscribeNowClick = (): void => {
        setIsSubscribing(true);
        window.location.href = installation.billing?.url ?? '';
    };

    const onSettingsClick = (): void => {
        props.onSettingsClick();
    };

    const onManageSubscriptionClick = (): void => {
        window.open(billing?.manageSubscriptionUrl!);
    };

    const onPaidConnectClick = (): void => {
        setIsConnectingToPaid(true);
        props.onPaidConnectClick();
    };

    const showDisconnectModalClick = (): void => {
        setShowDisconnectModal(true);
    };

    const closeDisconnectModalClick = (): void => {
        setShowDisconnectModal(false);
    };

    const deletePoller = (): void => {
        if (poller) {
            clearInterval(poller);
            poller = undefined;
        }
    };

    const showFreeTrialInformation = installation.freeTrial?.freeTrialEndDateUtc && !billing?.hasBought;
    const numberOfDaysRemaining = installation.freeTrial?.freeTrialRemainingDays;
    const hasFreeTrialExpired = numberOfDaysRemaining != null && numberOfDaysRemaining < 0;

    const showFreeTrialExpiredTag = hasFreeTrialExpired && !billing?.hasBought;
    const showExpiringTodayTag =
        numberOfDaysRemaining != null
            ? !hasFreeTrialExpired && !billing?.hasBought && numberOfDaysRemaining === 0
            : null;
    const showDaysRemainingTag =
        numberOfDaysRemaining != null ? !hasFreeTrialExpired && !billing?.hasBought && numberOfDaysRemaining > 0 : null;

    const freeTrialTag =
        showFreeTrialExpiredTag || showExpiringTodayTag || showDaysRemainingTag ? (
            <XUITag
                className='xui-margin-left'
                variant={numberOfDaysRemaining != null && numberOfDaysRemaining <= 3 ? 'negative' : 'warning'}
            >
                {showFreeTrialExpiredTag
                    ? integrationLang.CONNECTED_PAGE.FREE_TRIAL?.EXPIRED_TAG_TITLE
                    : showExpiringTodayTag
                      ? integrationLang.CONNECTED_PAGE.FREE_TRIAL?.EXPIRING_TODAY_TAG_TITLE
                      : showDaysRemainingTag
                        ? numberOfDaysRemaining + integrationLang.CONNECTED_PAGE.FREE_TRIAL?.REMAINING_DAYS_TAG_TITLE
                        : null}
            </XUITag>
        ) : null;

    if (isFetchingSettings) {
        return <XUILoader ariaLabel='Loading' />;
    }

    const subscribeNowButton =
        !hideShopifyNewConnectionActions && showFreeTrialInformation ? (
            <XUIButton
                qaHook='connected-subscribeNow-button'
                className='xui-button-fullwidth xui-margin-top-2xlarge'
                onClick={onSubscribeNowClick}
                isLoading={isSubscribing}
                loadingAriaLabel='Loading'
                variant='main'
            >
                {integrationLang.CONNECTED_PAGE.FREE_TRIAL?.SUBSCRIBE_NOW_BUTTON}
            </XUIButton>
        ) : null;

    const upgradeToPremiumButton =
        !hideShopifyNewConnectionActions && billing?.hasPaidPlan && billing.hasFreePlan && !billing.hasBought ? (
            <XUIButton
                qaHook='connected-upgradeApp-button'
                className='xui-button-fullwidth xui-margin-top-2xlarge'
                onClick={onPaidConnectClick}
                isLoading={isConnectingToPaid}
                loadingAriaLabel='Loading'
                variant='main'
            >
                {integrationLang.PAID_PLAN.BUTTON}
            </XUIButton>
        ) : null;

    const updateAppButton =
        !upgradeToPremiumButton && installation.versionSupport && installation.versionSupport.canUpdate ? (
            <XUIButton
                qaHook='connected-updateApp-button'
                onClick={onUpdateAppClick}
                className='xui-button-fullwidth xui-margin-top-2xlarge'
                variant='main'
            >
                {installation.integrationType === integrationTypes.SHOPIFY
                    ? lang.BUTTONS.SETUP_NOW
                    : lang.BUTTONS.UPDATE_APP}
            </XUIButton>
        ) : null;

    // show when free trial hasn't expired or they have bought in billing
    const settingsButtonIsMain = !upgradeToPremiumButton && !subscribeNowButton && !updateAppButton;
    const settingsButton =
        (!hasFreeTrialExpired || billing?.hasBought) && routes!.get(Pages.Settings) ? (
            <XUIButton
                qaHook='connected-settings-button'
                onClick={onSettingsClick}
                className={
                    settingsButtonIsMain
                        ? 'xui-button-fullwidth xui-margin-top-2xlarge'
                        : 'xui-button-fullwidth xui-margin-top'
                }
                variant={settingsButtonIsMain ? 'main' : 'standard'}
            >
                {installation.isUpgrading ? lang.BUTTONS.COMPLETE_SETUP : lang.BUTTONS.SETTINGS}
            </XUIButton>
        ) : null;

    const disconnectButton = (
        <XUIButton
            qaHook='connected-disconnect-button'
            data-testid='connected-disconnect-button'
            className='xui-button-fullwidth xui-margin-top'
            onClick={showDisconnectModalClick}
            loadingAriaLabel='Loading'
            variant='borderless-negative'
        >
            {lang.BUTTONS.DISCONNECT}
        </XUIButton>
    );
    const manageSubscriptionButton = billing?.manageSubscriptionUrl ? (
        <XUIButton
            qaHook='manage-subscription-button'
            data-testid='manage-subscription-button'
            onClick={onManageSubscriptionClick}
            className={'xui-button-fullwidth xui-margin-top'}
        >
            {lang.BUTTONS.MANAGE_SUBSCRIPTION} <XUIIcon icon={externalIcon} />
        </XUIButton>
    ) : null;
    const updateBanner = !!updateAppButton ? renderUpdatePanel() : null;

    const actions = (
        <>
            {updateAppButton}
            {upgradeToPremiumButton}
            {subscribeNowButton}
            {settingsButton}
            {manageSubscriptionButton}
            {disconnectButton}

            {integrationLang.ACCESS_PAGE.TAX_DISCLAIMER ? (
                <div className='content xui-padding-top-3xlarge'>
                    <TaxDisclaimer blurb={integrationLang.ACCESS_PAGE.TAX_DISCLAIMER} showIcon={true} />
                </div>
            ) : null}
        </>
    );

    const error =
        installation.state.includes(installationStates.ERRORED) ||
        installation.state.toUpperCase() === installationStates.UNHEALTHY
            ? (integrationLang.ERROR[installation.state] || lang.ERROR.CONNECTION_ERROR).replace(
                  'integrationName',
                  installation.displayName
              )
            : null;

    const successBanner = props.isSyncing && !error && (
        <div className='xui-margin-horizontal'>
            <InformationBanner
                action={
                    user.origin ? (
                        <XUIBannerAction
                            onClick={() => (window.location.href = integrationLang.CONNECTED_PAGE.SUCCESS_ACTION_URL)}
                        >
                            {integrationLang.CONNECTED_PAGE.SUCCESS_ACTION_LABEL}
                        </XUIBannerAction>
                    ) : undefined
                }
                qaHook='connected-success-banner'
                bannerMessage={
                    showFreeTrialInformation
                        ? integrationLang.CONNECTED_PAGE.FREE_TRIAL.SUCCESS_SETUP_MESSAGE
                        : integrationLang.CONNECTED_PAGE.SUCCESS_SETUP_MESSAGE
                }
                displayCloseButton={true}
                sentiment={'positive'}
            />
        </div>
    );

    const smartListsBanner =
        installation.allowSmartLists === false ? (
            <InformationBanner
                qaHook='connected-smartlists-banner'
                bannerMessage={
                    <>
                        <span>
                            {integrationLang.SMART_LISTS_RESTRICTION_BLURB_PART_1}
                            <a href={integrationLang.SMART_LISTS_RESTRICTION_ABSOLUTE_URL}>
                                {integrationLang.SMART_LISTS_RESTRICTION_DISPLAY_URL}
                            </a>
                            {integrationLang.SMART_LISTS_RESTRICTION_BLURB_PART_2}
                        </span>
                    </>
                }
                sentiment={'negative'}
            />
        ) : null;

    const pendingCancellationBanner = installation.billing?.subscriptionEndDate &&
        installation.billing?.manageSubscriptionUrl && (
            <PendingCancellationBanner
                bannerText={lang.PENDING_CANCELLATION_BANNER.CONTENT}
                confirmText={lang.BUTTONS.MANAGE_SUBSCRIPTION}
                headingText={lang.PENDING_CANCELLATION_BANNER.HEADING}
                subscriptionEndDate={convertTimezoneDisplayDate(
                    installation.billing.subscriptionEndDate,
                    organisation.timezone
                )}
                integrationName={integrationLang.DISPLAY_NAME}
                onManageSubscriptionClick={onManageSubscriptionClick}
                showExternalIcon={true}
            />
        );

    // if an integration has a paid plan, and the org has bought and does not have a pending expiry subscription we display the full modal
    // otherwise display a simple modal
    const disconnectAppModal =
        installation.billing?.hasPaidPlan &&
        installation.billing?.nextBillingDate &&
        !installation.billing?.subscriptionEndDate ? (
            <DisconnectAppModal
                showModal={showDisconnectModal}
                onSecondaryClicked={closeDisconnectModalClick}
                onPrimaryClicked={onDisconnectClick}
                isLoading={isDisconnecting}
            />
        ) : (
            <DeleteModal
                showModal={showDisconnectModal}
                onSecondaryClicked={closeDisconnectModalClick}
                onPrimaryClicked={() => onDisconnectClick(false)}
                cancelButtonText={lang?.MODALS?.DISCONNECT_APP?.CLOSE_BUTTON}
                deleteButtonText={lang?.MODALS?.DISCONNECT_APP?.CONFIRM_BUTTON}
                title={lang?.MODALS?.DISCONNECT_APP?.MODAL_HEADER}
                isLoading={isDisconnecting}
                qaHook='simple-disconnect-modal'
            >
                {translateWithIntegrationName(
                    lang?.MODALS?.DISCONNECT_APP?.MODAL_MESSAGE_UNSUBSCRIBED,
                    integrationLang?.DISPLAY_NAME
                )}
            </DeleteModal>
        );

    return (
        <>
            <XUIPageHeader
                breadcrumb={
                    <XUIBreadcrumbTrail breadcrumbs={[{ key: 'a', label: lang.HEADINGS.APPS, href: lang.URLS.APPS }]} />
                }
                title={installation.displayName}
            />
            <div
                data-automationid='connected-page'
                className='xui-page-width-standard xui-clearfix xui-padding-vertical-3xlarge'
            >
                {shopifyDecommissionBanner}
                {successBanner}
                {pendingCancellationBanner}
                {updateBanner}
                <Overview
                    title={installation.displayName}
                    freeTrialTag={freeTrialTag}
                    type={installation.integrationType}
                    tags={integrationLang.TAGS}
                    blurb={integrationLang.CONNECTED_PAGE.BLURB}
                    error={error}
                    onSettingsClick={onSettingsClick}
                    actions={actions}
                >
                    {smartListsBanner}
                    <div className='xui-page-width-standard'>
                        <div className='xui-margin-auto activity-table'>
                            <ActivityTable installation={installation} integrationEvents={events} />
                        </div>
                    </div>
                </Overview>
            </div>
            {disconnectAppModal}
        </>
    );
};

export default withLDConsumer()(Connected);
